import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HomeBackground from "../../assets/images/HomeBackground.png";

// Features Data
const featuresData = [
  {
    id: 1,
    icon: <EmojiEventsIcon />,
    title: "Best Industry Leaders",
    description: "We provide you the opportunity to learn from top industry leaders, offering you practical knowledge and real-world insights. Your success is our top priority."
  },
  {
    id: 2,
    icon: <PeopleIcon />,
    title: "Transforming Lives",
    description: "Trained over 1 lakh students. Empowering careers and building a skilled future for all."
  },
  {
    id: 3,
    icon: <WorkspacePremiumIcon />,
    title: "Professional Certification",
    description: "Learn online at your own pace, with the flexibility to study whenever and wherever suits you. Master new skills on your terms, without the pressure of a traditional classroom."
  }
];

// Styled Components
const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '700px',
  backgroundImage: `url(${HomeBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.down('md')]: {
    minHeight: '600px',
    padding: theme.spacing(6, 0),
    backgroundAttachment: 'scroll',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    padding: theme.spacing(4, 0),
  },
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(135deg, rgba(14, 43, 81, 0.92) 0%, rgba(26, 61, 107, 0.88) 100%)',
  zIndex: 1,
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  width: '100%',
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 800,
  color: '#f7c42d',
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  fontFamily: 'Hind Mysuru, sans-serif',
  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
    marginBottom: theme.spacing(2),
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  lineHeight: 1.8,
  color: 'rgba(255, 255, 255, 0.95)',
  textAlign: 'center',
  maxWidth: '900px',
  margin: '0 auto',
  marginBottom: theme.spacing(6),
  fontFamily: 'Hind Mysuru, sans-serif',
  fontWeight: 500,
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    marginBottom: theme.spacing(4),
    maxWidth: '700px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(3),
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  border: '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(247, 196, 45, 0.3)',
    borderColor: '#f7c42d',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '70px',
  height: '70px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #f7c42d 0%, #e5b327 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 15px rgba(247, 196, 45, 0.3)',
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
    color: '#0e2b51',
  },
  [theme.breakpoints.down('sm')]: {
    width: '60px',
    height: '60px',
    '& .MuiSvgIcon-root': {
      fontSize: '1.75rem',
    },
  },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.4rem',
  fontWeight: 700,
  color: '#0e2b51',
  marginBottom: theme.spacing(1.5),
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
  },
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  lineHeight: 1.7,
  color: '#4a4a4a',
  fontFamily: 'Hind Mysuru, sans-serif',
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
}));

const HighlightStats = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(6),
  marginTop: theme.spacing(6),
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '12px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(247, 196, 45, 0.3)',
  minWidth: '150px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '120px',
  },
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 800,
  color: '#f7c42d',
  marginBottom: theme.spacing(0.5),
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 600,
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const SecondInfo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SectionContainer>
      <Overlay />
      <ContentWrapper>
        <Container maxWidth="lg">
          {/* Header Section */}
          <MainTitle>Why Choose Us?</MainTitle>
          
          <Description>
            Choose SkillServe for NSDC-approved certifications and industry-recognized credentials that align with professional standards. Our NSQF-aligned courses provide high-quality, job-focused skills to prepare you for real-world challenges. With a proven track record of training over 1 lakh students, we ensure practical learning through experienced trainers.
          </Description>

          {/* Feature Cards */}
          <Grid container spacing={3}>
            {featuresData.map((feature) => (
              <Grid item xs={12} sm={6} md={4} key={feature.id}>
                <FeatureCard>
                  <CardContent sx={{ padding: { xs: 3, md: 4 } }}>
                    <IconWrapper>
                      {feature.icon}
                    </IconWrapper>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>

          {/* Stats Section */}
          <HighlightStats>
            <StatItem>
              <StatNumber>1L+</StatNumber>
              <StatLabel>Students Trained</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>100+</StatNumber>
              <StatLabel>Expert Trainers</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>50+</StatNumber>
              <StatLabel>Industry Courses</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>95%</StatNumber>
              <StatLabel>Success Rate</StatLabel>
            </StatItem>
          </HighlightStats>
        </Container>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default SecondInfo;

